import { Check } from "lucide-react-native";
import { FC, useEffect } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import { Box, HStack, Pressable, Skeleton, Text } from "@/components/ui/base";

const CIRCLE = 28;

interface StepIndicatorProps {
  completedSteps?: number[];
  current: number;
  loading?: boolean;
  onStepPress?: (step: number) => void;
  steps: string[];
}

interface AnimatedBarProps {
  active: boolean;
  done: boolean;
}

const BAR_W = 32;
const BAR_H = 2;

const BAR_BG = "#e4e4e7";
const BAR_ACTIVE = "#8b5cf6";
const BAR_DONE = "#0d0f1b";

const AnimatedBar: FC<AnimatedBarProps> = ({ active, done }) => {
  const filled = active || done;
  const translateX = useSharedValue(filled ? 0 : -BAR_W);

  useEffect(() => {
    translateX.value = withTiming(filled ? 0 : -BAR_W, { duration: 500 });
  }, [filled, translateX]);

  const fillColor = active ? BAR_ACTIVE : BAR_DONE;

  const fillStyle = useAnimatedStyle(() => ({
    backgroundColor: fillColor,
    height: BAR_H,
    transform: [{ translateX: translateX.value }],
    width: BAR_W,
  }));

  return (
    <Box
      style={{
        backgroundColor: BAR_BG,
        height: BAR_H,
        overflow: "hidden",
        width: BAR_W,
      }}
    >
      <Animated.View style={fillStyle} />
    </Box>
  );
};

const StepIndicator: FC<StepIndicatorProps> = ({
  completedSteps,
  current,
  loading = false,
  onStepPress,
  steps,
}) => {
  return (
    <HStack className="items-center gap-2">
      {steps.map((label, i) => {
        const active = i === current;
        const done = completedSteps?.includes(i) ?? false;
        const tappable = !loading && !!onStepPress && i !== current && done;

        const bar = loading ? (
          <Skeleton style={{ height: BAR_H, width: BAR_W }} variant="sharp" />
        ) : (
          <AnimatedBar active={active} done={done} />
        );

        const circle = loading ? (
          <Skeleton
            style={{
              borderRadius: CIRCLE,
              height: CIRCLE,
              width: CIRCLE,
            }}
          />
        ) : (
          <Box
            className={`items-center justify-center ${
              active
                ? "bg-primary-500"
                : done
                  ? "bg-tertiary-800"
                  : "bg-background-100"
            }`}
            style={{
              borderRadius: CIRCLE,
              height: CIRCLE,
              width: CIRCLE,
            }}
          >
            {done && !active ? (
              <Check color="#fff" size={14} />
            ) : (
              <Text
                className={`text-xs font-semibold ${
                  active ? "text-white" : "text-typography-400"
                }`}
              >
                {i + 1}
              </Text>
            )}
          </Box>
        );

        const text = loading ? (
          <Skeleton style={{ height: 16, minWidth: 80, width: 80 }} />
        ) : (
          <Text
            className={`text-sm ${
              active
                ? "font-semibold text-primary-500"
                : done
                  ? "font-medium text-typography-900 hidden lg:flex"
                  : "text-typography-400 hidden lg:flex"
            }`}
          >
            {label}
          </Text>
        );

        const content = (
          <HStack className="items-center gap-2">
            {i > 0 && bar}
            <HStack className="items-center gap-2">
              {circle}
              {text}
            </HStack>
          </HStack>
        );

        return tappable ? (
          <Pressable key={label} onPress={() => onStepPress(i)}>
            {content}
          </Pressable>
        ) : (
          <Box key={label}>{content}</Box>
        );
      })}
    </HStack>
  );
};

export default StepIndicator;
